// Улучшенный компонент CarCard.js
import React, { useState, useEffect, useRef } from 'react';
import { ExternalLink, Heart } from 'lucide-react';

const CarCard = ({ car, onOpenApplicationModal, onOpenDetailsModal, index = 0, activeTab = 'home' }) => {
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [imageError, setImageError] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const imageRef = useRef(null);
    const cardRef = useRef(null);
    const intersectionObserverRef = useRef(null);

    // Отслеживаем изменение размера окна
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Оптимизация URL изображения
    const optimizeImageUrl = (url) => {
        if (!url || !isMobile) return url;
        if (url.startsWith('data:') || url.includes('.svg')) return url;
        return url;
    };

    // Используем оптимизированный URL
    const displayImage = optimizeImageUrl(car.image);

    // Ленивая загрузка изображений с IntersectionObserver
    useEffect(() => {
        if (!('IntersectionObserver' in window)) return;

        // Очищаем предыдущий observer при каждом вызове эффекта
        if (intersectionObserverRef.current && cardRef.current) {
            intersectionObserverRef.current.unobserve(cardRef.current);
        }

        // Приоритетная загрузка для первых 6 карточек на главной странице
        const isPriorityCard = isMobile && activeTab === 'home' && index < 6;

        if (isPriorityCard && imageRef.current && !imageRef.current.src) {
            imageRef.current.src = displayImage;
            return;
        }

        // Обычная ленивая загрузка для других карточек
        intersectionObserverRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && imageRef.current && !imageRef.current.src) {
                        imageRef.current.src = displayImage;
                    }
                });
            },
            {
                rootMargin: '200px',
                threshold: 0.1
            }
        );

        if (cardRef.current) {
            intersectionObserverRef.current.observe(cardRef.current);
        }

        // Очистка при размонтировании
        return () => {
            if (intersectionObserverRef.current && cardRef.current) {
                intersectionObserverRef.current.unobserve(cardRef.current);
                intersectionObserverRef.current = null;
            }
        };
    }, [displayImage, isMobile, activeTab, index]);

    // Обработчики событий
    const handleApplicationClick = (e) => {
        e.stopPropagation();
        onOpenApplicationModal(car);
    };

    const handleCardClick = () => {
        onOpenDetailsModal(car);
    };

    const handleFavoriteClick = (e) => {
        e.stopPropagation();
        setIsFavorite(!isFavorite);
        // Здесь можно добавить логику для сохранения избранного в localStorage
    };

    const handleImageLoad = () => {
        setIsImageLoaded(true);
    };

    const handleImageError = () => {
        setImageError(true);
        setIsImageLoaded(true);
    };    // Форматирование цены
    const formatPrice = (price) => {
        // Проверяем, что price не null и не undefined
        if (price === null || price === undefined || isNaN(price)) {
            return '₽0';
        }
        
        if (isMobile && price >= 1000000) {
            return `${Math.round(price / 1000000 * 10) / 10} млн ₽`;
        } else {
            return `₽${Math.round(Number(price)).toLocaleString()}`;
        }
    };// Форматирование пробега для мобильных
    const formatMileage = (mileage) => {
        // Проверяем, что mileage не null и не undefined
        if (mileage === null || mileage === undefined || isNaN(mileage)) {
            return '0 км';
        }
        
        if (isMobile && mileage >= 10000) {
            return `${Math.floor(mileage / 1000)}т км`;
        }
        return `${Number(mileage).toLocaleString()} км`;
    };

    // Эмодзи для характеристик
    const getEmoji = (type) => {
        switch(type) {
            case 'engine': return '🔧';
            case 'transmission': return '⚙️';
            case 'mileage': return '🛣️';
            case 'fuelType': return '⛽';
            case 'color': return '🎨';
            case 'driveType': return '🚗';
            default: return '📋';
        }
    };

    return (
        <div
            ref={cardRef}
            className="car-card-improved"
            onClick={handleCardClick}
        >
            <div className="car-image-container">
                {!isImageLoaded && !imageError && (
                    <div className="car-image-placeholder">
                        <div className="loading-spinner-improved"></div>
                    </div>
                )}
                <img
                    ref={imageRef}
                    alt={`${car.brand} ${car.model}`}
                    className={`car-image ${isImageLoaded ? 'loaded' : ''}`}
                    data-src={displayImage}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    loading="lazy"
                />
                {imageError && (
                    <div className="image-error-placeholder">
                        <span>Фото недоступно</span>
                    </div>
                )}

                {/* Индикатор количества фото и кнопка избранного */}
                <div className="image-overlay">
                    {car.hasMultipleImages && (
                        <div className="car-image-count">
                            <span>+{car.imageCount - 1} фото</span>
                        </div>
                    )}

                    <button
                        className={`favorite-button ${isFavorite ? 'active' : ''}`}
                        onClick={handleFavoriteClick}
                        aria-label={isFavorite ? "Удалить из избранного" : "Добавить в избранное"}
                    >
                        <Heart size={18} fill={isFavorite ? "#ff4757" : "none"} color={isFavorite ? "#ff4757" : "white"} />
                    </button>
                </div>

                {/* Шильдик о цене */}
                <div className="car-price-tag">
                    <span>{formatPrice(car.price)}</span>
                </div>
            </div>

            <div className="car-details-improved">
                <h3 className="car-title-improved">{car.brand} {car.model}</h3>

                <div className="car-year-improved">
                    <span>{car.year} г.</span>
                </div>

                <div className="car-specs-preview-improved">
                    {car.engineVolume && (
                        <div className="car-spec-preview-improved">
                            <span className="car-spec-icon">{getEmoji('engine')}</span>
                            <span>{car.engineVolume} л</span>
                        </div>
                    )}
                    {car.transmission && (
                        <div className="car-spec-preview-improved">
                            <span className="car-spec-icon">{getEmoji('transmission')}</span>
                            <span>{car.transmission}</span>
                        </div>
                    )}
                    {car.mileage !== undefined && (
                        <div className="car-spec-preview-improved">
                            <span className="car-spec-icon">{getEmoji('mileage')}</span>
                            <span>{formatMileage(car.mileage)}</span>
                        </div>
                    )}
                    {car.fuelType && (
                        <div className="car-spec-preview-improved">
                            <span className="car-spec-icon">{getEmoji('fuelType')}</span>
                            <span>{car.fuelType}</span>
                        </div>
                    )}
                </div>

                {car.description && (
                    <p className="car-description-preview">
                        {car.description.length > 80
                            ? `${car.description.substring(0, 77)}...`
                            : car.description}
                    </p>
                )}

                <div className="car-card-actions-improved">
                    <button
                        className="button-primary-improved"
                        onClick={(e) => {
                            e.stopPropagation();
                            onOpenDetailsModal(car);
                        }}
                    >
                        <span>Подробнее</span>
                        <ExternalLink size={16} />
                    </button>
                    <button
                        className="button-secondary-improved"
                        onClick={handleApplicationClick}
                    >
                        Оставить заявку
                    </button>
                </div>
            </div>
        </div>
    );
};

export default React.memo(CarCard);
